import { useEffect, useRef } from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useMe } from "@/features/auth/useMe";
import { ApiError } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { getPublicNavCapabilities } from "@/features/navigation/navCapabilities";
import { canonicalFeedbackPreviewEnabled } from '@/features/feedback-integration/feedbackPreview';
import { CanonicalCoachWorkspaceHost } from '@/features/feedback-integration/CanonicalCoachWorkspaceHost';
import {
  UploadQueueProvider,
  useUploadQueueContext,
} from "./components/UploadQueueContext";
import { UploadQueuePanel } from "./components/UploadQueuePanel";

/** Shared route lifetime for /coach and /coach/review. Keep files and the
 * active chunk loop alive across every coaching tab, including Review. */
export function CoachWorkspaceLayout() {
  const me = useMe();
  const navigate = useNavigate({ from: "/coach" });
  const authRedirectFired = useRef(false);
  const authExpired = me.error instanceof ApiError && me.error.status === 401;
  const anonymous = me.data !== undefined && !me.data?.user;
  const showLogin = authExpired || anonymous;
  useEffect(() => {
    if (!showLogin || authRedirectFired.current) return;
    authRedirectFired.current = true;
    // This parent survives child-route changes. Capture the current child,
    // not the route where an otherwise long-lived upload queue first mounted.
    const next =
      typeof window === "undefined"
        ? "/coach"
        : `${window.location.pathname}${window.location.search}`;
    void navigate({
      to: "/login",
      search: { next },
      replace: true,
    });
  }, [showLogin, navigate]);
  // The materialized identity clears synchronously at logout/401, even while
  // the query retains its last successful payload. Reset the active engine then.
  const actorId = useAuthStore((state) => state.user?.id);
  const clubId = useAuthStore((state) => state.currentClub?.id);
  // Own the auth redirect here: mounting a child useMe observer after a 401
  // refetches the failed query, making this parent pending and unmounting the
  // child before its redirect can run. Do not mount an anonymous outlet.
  if (showLogin) return null;
  if (me.data !== undefined && !getPublicNavCapabilities(me.data).canOpenCoach) {
    // Each page owns its existing role redirect. Stop uploads immediately
    // when that access disappears, rather than retaining them during redirect.
    return <Outlet />;
  }
  // Children own their initial loading state. Keep their identity observers
  // and queue context mounted through pending/network errors: hiding the
  // outlet on every pending refetch would remount it after each failure and
  // start another request indefinitely. The identity key still resets files
  // and the active engine when the authenticated user or host club changes.
  return (
    <UploadQueueProvider key={`${actorId ?? ""}:${clubId ?? ""}`}>
      {canonicalFeedbackPreviewEnabled()?<CanonicalCoachWorkspaceHost><Outlet /></CanonicalCoachWorkspaceHost>:<Outlet />}
      <CoachUploadQueuePanel />
    </UploadQueueProvider>
  );
}

function CoachUploadQueuePanel() {
  const queue = useUploadQueueContext();
  return <UploadQueuePanel queue={queue} />;
}
